import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@goodday-clone/ui'
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet } from 'lucide-react'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'

export interface ImportResult {
  imported: number
  failed: number
  total: number
  errors: { row: number; reason: string }[]
}

interface ImportExcelModalProps {
  isOpen: boolean
  onClose: () => void
  kind: 'leads' | 'deals'
  /** Called after a run that added at least one row, so the list can refresh. */
  onImported: (result: ImportResult) => void
}

const WORD = { leads: 'lead', deals: 'deal' } as const
const COLUMNS = {
  leads: ['Lead Name', 'Company', 'Contact Person', 'Email', 'Phone', 'Source', 'Status', 'Interest', 'Value', 'Assignee', 'Industry', 'Services', 'Notes'],
  deals: ['Deal Title', 'Client', 'Stage', 'Priority', 'Value', 'Probability', 'Owner', 'Source', 'Close Date', 'Contact Person', 'Services', 'Notes'],
} as const

export function ImportExcelModal({ isOpen, onClose, kind, onImported }: ImportExcelModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reset = (open: boolean) => {
    if (open) return
    setFile(null)
    setResult(null)
    setError(null)
    onClose()
  }

  const downloadTemplate = async () => {
    setDownloading(true)
    setError(null)
    try {
      const blob = await api.get<Blob>(`/${kind}/import/template`, undefined, { responseType: 'blob', skipErrorToast: true })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${kind}-import-template.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(getApiErrorMessage(err, 'download the template'))
    } finally {
      setDownloading(false)
    }
  }

  const submit = async () => {
    if (!file) return
    setSaving(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const response = await api.post<ImportResult>(`/${kind}/import`, form, { headers: { 'Content-Type': null }, skipErrorToast: true })
      setResult(response)
      if (response.imported > 0) onImported(response)
    } catch (err) {
      setError(getApiErrorMessage(err, `import ${kind}`))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={reset}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import {kind} from Excel</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            One {WORD[kind]} per row. The columns are read by heading, so use these names — the first row of your sheet is the headings.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {COLUMNS[kind].map((column) => (
              <span key={column} className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {column}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border p-3">
            <label className="flex min-w-0 cursor-pointer items-center gap-2 text-sm">
              <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{file ? file.name : 'No file chosen'}</span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="sr-only"
                aria-label={`${WORD[kind]} spreadsheet`}
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null)
                  setResult(null)
                  setError(null)
                }}
              />
            </label>
            <Button variant="ghost" size="sm" onClick={downloadTemplate} disabled={downloading} className="shrink-0 gap-1.5">
              <Download className="h-4 w-4" />
              {downloading ? 'Preparing…' : 'Template'}
            </Button>
          </div>

          {result && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                {result.imported} {WORD[kind]}
                {result.imported === 1 ? '' : 's'} added
                {result.failed > 0 && `, ${result.failed} skipped`}
              </p>
              {result.errors.length > 0 && (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                  {result.errors.map((e) => (
                    <li key={e.row} className="flex gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                      <span>
                        Row {e.row}: {e.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)} disabled={saving}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          <Button onClick={submit} disabled={!file || saving}>
            {saving ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
